/**
 * Wizard step 4 — the summary and "Submit for review" (M6 §5).
 *
 * Two blocks: what will be published (the form, as it will be stored) and what the
 * automatic checks have said *so far*. "So far" is the honest word — validation is a Celery
 * task that starts at finalize, so a small file usually has its checks by the time the
 * reader gets here and a 4 GB one may not. Either way the submit is allowed: the queue is
 * where a decision is made, not this step.
 */
import { useTranslation } from 'react-i18next';

import type { Domain, ManagedDatasetRow } from '../../lib/api';
import { CHECK_GLYPH, checkState, checkTally, fmtBytes, type WizardState } from './manager';

export interface ReviewStepProps {
  state: WizardState;
  domains: Domain[];
  /** The catalogue, so the new-version target can be named rather than printed as an id. */
  datasets?: ManagedDatasetRow[];
}

export function ReviewStep({ state, domains, datasets = [] }: ReviewStepProps) {
  const { t } = useTranslation();
  const f = state.form;
  const tally = checkTally(state.row);
  const domain = domains.find((d) => d.id === f.domain);
  const rows: Array<[string, string]> = [
    [t('manager.f.title'), f.title],
    [t('manager.f.domain'), domain?.name ?? f.domain],
    [t('manager.f.source'), f.source],
    [t('manager.f.licence'), f.licence],
    [t('manager.f.cadence'), f.cadence ? t(`manager.cadence.${f.cadence}`) : ''],
    [t('manager.f.abstract'), f.abstract],
    [t('manager.f.lineage'), f.lineage],
    [t('manager.f.sourceDetail'), f.source_detail],
    [t('manager.f.captureMethod'), f.capture_method],
    [t('manager.f.accuracy'), f.accuracy],
    [t('manager.f.resolution'), f.resolution],
    [t('manager.f.contact'), f.contact],
    [t('manager.f.classification'), f.classification ? t(`manager.cls.${f.classification}`) : ''],
    [t('manager.f.websiteVisible'), f.websiteVisible ? t('manager.visible.yes') : t('manager.visible.no')],
  ];
  // The two facts a reader would otherwise have to walk back two steps to check: whether the
  // raster was flagged categorical, and which dataset this upload replaces. Both are
  // appended rather than woven in, because both are absent on an ordinary new upload and an
  // empty row would say nothing.
  if (state.row?.format === 'geotiff') {
    rows.push([t('manager.f.isCategorical'), f.isCategorical ? t('manager.yes') : t('manager.no')]);
  }
  if (state.versionOf) {
    const target = datasets.find((d) => d.id === state.versionOf);
    rows.push([t('manager.versionOf.label'), target?.title ?? state.versionOf]);
  }

  return (
    <div className="mgr-step mgr-step-four" data-testid="mgr-step-4">
      <section className="mgr-review-block">
        <h4>{t('manager.review.summary')}</h4>
        <table className="mini" data-testid="mgr-review-summary">
          <tbody>
            <tr>
              <th scope="row">{state.file?.name ?? ''}</th>
              <td>{state.file ? fmtBytes(state.file.size) : ''}</td>
            </tr>
            {rows
              .filter(([, value]) => value !== '')
              .map(([label, value]) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  <td>{value}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      <section className="mgr-review-block">
        <h4>{t('manager.review.checks')}</h4>
        <div data-testid="mgr-review-checks">
          {tally.total > 0 ? (
            <>
              <p className="mgr-tally">
                {t('manager.review.tally', { passed: tally.passed, total: tally.total })}
              </p>
              <ul className="mgr-checks">
                {(state.row?.checks ?? []).map((check) => (
                  <li key={check.name} className={checkState(check)} data-testid={`mgr-step-check-${check.name}`}>
                    {/* The same three-state glyph the review modal uses (`CHECK_GLYPH`): an
                        advisory that did not pass is an amber ○, not the blocking ✗. Painting
                        both red told the submitter a multi-band GeoTIFF had failed, when the
                        validator had simply kept band 1 and said so — and re-uploading is not
                        the fix for something that is not broken. */}
                    <span className="mgr-check-glyph" aria-hidden="true">
                      {CHECK_GLYPH[checkState(check)]}
                    </span>
                    {/* The verdict in words — the glyph is `aria-hidden` and its three states
                        differ only by colour (`ReviewModal` carries the same line). */}
                    <span className="sr-only">
                      {t(`manager.checkState.${checkState(check)}`)}
                    </span>
                    {/* The server's check names are identifiers (`crs_present`); the
                        translation is the sentence a reader wants, and an unknown name —
                        a check added on the server before this table learns about it —
                        falls back to the identifier rather than to a blank. */}
                    <span>{t(`manager.check.${check.name}`, { defaultValue: check.name })}</span>
                    {check.detail ? <em>{check.detail}</em> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mgr-hint">{t('manager.review.pending')}</p>
          )}
        </div>
      </section>
    </div>
  );
}
